'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Amount, InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { format } from '@morpho-org/morpho-ts';
import { AvatarMultichain } from '@morpho-org/ui-v6/client';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { Chevron } from '@repo/ui/icons';
import React, { type InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAssetInput } from '../useAssetInput';
import SelectTokenInputMax from './SelectTokenInputMax';
import SelectTokenPriceUsd from './SelectTokenPriceUsd';
import SelectTokenTextInput from './SelectTokenTextInput';
function toUsdNumber(token: InterfaceToken, amount: Amount, usdValue: bigint | undefined) {
  const number = Number(format.number.digits(2).trailingZero(false).locale('en-US').of(usdValue ?? token.toUsd(amount ?? ZERO), 18));
  if (isNaN(number)) throw new Error('Invalid number');
  return number;
}
interface SelectTokenInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  className?: string;
  header: string;
  token: InterfaceToken;
  amount: Amount;
  usdValue?: bigint;
  maxValue?: Amount;
  onMaxClick?(): void;
  onChange(value: bigint): void;
  onPendingChange?: (isPending: boolean) => void;
  debouncedTime?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  selectedTokenTicker: string;
  selectedTokenLogoURI: string | null;
  selectedTokenChain: {
    id: ChainId;
    name: string;
  };
  onSelectTokenClick(): void;
  footerExtra?: React.ReactNode;
  isLoading?: boolean;
  isMaxDisplayed?: boolean;
  hasError?: boolean;
  testId?: string;
}
const SelectTokenInput = t0 => {
  const $ = _c(87);
  const {
    className,
    header,
    token,
    debouncedTime,
    amount,
    usdValue,
    maxValue,
    disabled,
    onChange,
    onPendingChange,
    onMaxClick,
    isMaxDisplayed,
    autoFocus,
    selectedTokenTicker,
    selectedTokenLogoURI,
    selectedTokenChain,
    onSelectTokenClick,
    footerExtra,
    isLoading,
    hasError,
    testId
  } = t0;
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== amount || $[1] !== debouncedTime || $[2] !== onChange || $[3] !== token.decimals) {
    t1 = {
      value: amount,
      decimals: token.decimals,
      debouncedTime,
      onChange
    };
    $[0] = amount;
    $[1] = debouncedTime;
    $[2] = onChange;
    $[3] = token.decimals;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    handleChange,
    valueStr,
    isPending,
    flush
  } = useAssetInput(t1);
  let t2;
  let t3;
  if ($[5] !== isPending || $[6] !== onPendingChange) {
    t2 = () => {
      onPendingChange?.(isPending);
      return () => onPendingChange?.(false);
    };
    t3 = [isPending, onPendingChange];
    $[5] = isPending;
    $[6] = onPendingChange;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== amount || $[10] !== token || $[11] !== usdValue) {
    t4 = formatters.short.unit("$").digits(2).trailingZero(false).of(usdValue ?? token.toUsd(amount ?? ZERO), 18);
    $[9] = amount;
    $[10] = token;
    $[11] = usdValue;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const amountPriceUsd = t4;
  let t5;
  try {
    let t6;
    if ($[13] !== amount || $[14] !== token || $[15] !== usdValue) {
      t6 = toUsdNumber(token, amount, usdValue);
      $[13] = amount;
      $[14] = token;
      $[15] = usdValue;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = t6;
  } catch {
    t5 = undefined;
  }
  const usdNumber = t5;
  let t6;
  if ($[17] !== disabled || $[18] !== onMaxClick) {
    t6 = () => !disabled ? onMaxClick?.() : null;
    $[17] = disabled;
    $[18] = onMaxClick;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleMaxClick = t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setFocused(true);
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleFocus = t7;
  let t8;
  if ($[21] !== flush) {
    t8 = () => {
      flush();
      setFocused(false);
    };
    $[21] = flush;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleBlur = t8;
  let t9;
  if ($[23] !== handleChange) {
    t9 = event => {
      handleChange(event.target.value);
    };
    $[23] = handleChange;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleInputChange = t9;
  let t10;
  if ($[25] !== className) {
    t10 = cn("flex flex-col gap-[9px]", className);
    $[25] = className;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = focused ? "bg-card border-ring" : "bg-muted";
  const t12 = !focused && valueStr && "border-border";
  const t13 = !focused && !valueStr && "border-transparent";
  const t14 = disabled ? "cursor-not-allowed" : "cursor-text";
  let t15;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = cn("gap-sm p-md border-[0.5px] flex flex-col rounded-[20px]", t11, t12, t13, t14);
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== disabled) {
    t16 = () => !disabled && inputRef.current?.focus();
    $[32] = disabled;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = hasError ? "text-destructive-foreground" : "text-secondary-foreground";
  let t18;
  if ($[34] !== t17) {
    t18 = cn("text-xs max-w-[170px] truncate", t17);
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== header || $[37] !== t18) {
    t19 = <div className="gap-sm flex flex-row items-center justify-between"><span className={t18}>{header}</span></div>;
    $[36] = header;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== autoFocus || $[40] !== disabled || $[41] !== handleBlur || $[42] !== handleInputChange || $[43] !== hasError || $[44] !== isLoading || $[45] !== testId || $[46] !== valueStr) {
    t20 = <SelectTokenTextInput ref={inputRef} onFocus={handleFocus} onBlur={handleBlur} onChange={handleInputChange} value={valueStr} disabled={disabled} autoFocus={autoFocus} isLoading={isLoading} hasError={hasError} testId={testId} />;
    $[39] = autoFocus;
    $[40] = disabled;
    $[41] = handleBlur;
    $[42] = handleInputChange;
    $[43] = hasError;
    $[44] = isLoading;
    $[45] = testId;
    $[46] = valueStr;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = `${testId}-select-token-button`;
  let t22;
  if ($[48] !== selectedTokenChain || $[49] !== selectedTokenLogoURI) {
    t22 = <AvatarMultichain src={selectedTokenLogoURI} chain={selectedTokenChain} size={16} networkSize={8} />;
    $[48] = selectedTokenChain;
    $[49] = selectedTokenLogoURI;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Chevron />;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== onSelectTokenClick || $[53] !== selectedTokenTicker || $[54] !== t21 || $[55] !== t22) {
    t24 = <Button onClick={onSelectTokenClick} variant="secondary" data-testid={t21}>{t22}{selectedTokenTicker}{t23}</Button>;
    $[52] = onSelectTokenClick;
    $[53] = selectedTokenTicker;
    $[54] = t21;
    $[55] = t22;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t20 || $[58] !== t24) {
    t25 = <div className="gap-sm flex flex-row items-center justify-between">{t20}{t24}</div>;
    $[57] = t20;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== amountPriceUsd || $[61] !== disabled || $[62] !== focused || $[63] !== footerExtra || $[64] !== hasError || $[65] !== isLoading || $[66] !== usdNumber) {
    t26 = <SelectTokenPriceUsd focused={focused} disabled={disabled} usdNumber={usdNumber} footerExtra={footerExtra} amountPriceUsd={amountPriceUsd} isLoading={isLoading} hasError={hasError} />;
    $[60] = amountPriceUsd;
    $[61] = disabled;
    $[62] = focused;
    $[63] = footerExtra;
    $[64] = hasError;
    $[65] = isLoading;
    $[66] = usdNumber;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== disabled || $[69] !== handleMaxClick || $[70] !== isLoading || $[71] !== isMaxDisplayed || $[72] !== maxValue || $[73] !== token) {
    t27 = isMaxDisplayed && <SelectTokenInputMax isLoading={isLoading} token={token} maxValue={maxValue} onMaxClick={handleMaxClick} disabled={disabled} />;
    $[68] = disabled;
    $[69] = handleMaxClick;
    $[70] = isLoading;
    $[71] = isMaxDisplayed;
    $[72] = maxValue;
    $[73] = token;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t26 || $[76] !== t27) {
    t28 = <div className="gap-sm flex flex-row items-center justify-between">{t26}{t27}</div>;
    $[75] = t26;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t15 || $[79] !== t16 || $[80] !== t19 || $[81] !== t25 || $[82] !== t28) {
    t29 = <div className={t15} onClick={t16}>{t19}{t25}{t28}</div>;
    $[78] = t15;
    $[79] = t16;
    $[80] = t19;
    $[81] = t25;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== t10 || $[85] !== t29) {
    t30 = <div className={t10}>{t29}</div>;
    $[84] = t10;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  return t30;
};
export default SelectTokenInput;